import {
  Box,
  Grid,
  Pagination,
  Stack,
  Typography
} from '@mui/material';
import MainCard from 'components/MainCard';
import ProviderChip from 'components/ProviderChip';
import { apiSlice } from 'features/api/apiSlice';
import { getValue, normalizeCursorPage, toNumber } from 'helpers/PatientHealthDataHelper';
import PatientDateRangeSelector from 'pages/patients/tabs/PatientDateRangeSelector';
import type { PatientDateRange } from 'pages/patients/tabs/PatientDateRangeSelector';
import { Dumbbell, Flame, Heart, MoveHorizontal, Timer } from 'lucide-react';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { store } from 'store';
import { getWorkoutStyle } from 'utils/workout-styles';
import i18n from 'app/i18n';

export type WorkoutDayRange = PatientDateRange;

type WorkoutRecord = Record<string, unknown>;

export function getWorkoutDateRange(range: WorkoutDayRange): { start: moment.Moment; end: moment.Moment } {
  return {
    start: moment().subtract(range, 'days').startOf('day'),
    end: moment().endOf('day')
  };
}

type WorkoutSummary = {
  count: number;
  totalDuration: number;
  totalCalories: number;
  totalDistance: number;
};

interface WorkoutsTabProps {
  userId: string;
}

function formatDuration(totalSeconds: number): string {
  if (!Number.isFinite(totalSeconds) || totalSeconds <= 0) return '—';

  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = Math.floor(totalSeconds % 60);

  if (hours > 0) return `${hours}h ${minutes}m`;
  if (minutes > 0) return `${minutes}m ${seconds}s`;
  return `${seconds}s`;
}

function formatDistance(meters: number): string {
  if (!Number.isFinite(meters) || meters <= 0) return '0.0 km';
  return `${(meters / 1000).toFixed(1)} km`;
}

function useWorkoutSummaryData(userId: string, startOfRange: moment.Moment, endOfRange: moment.Moment) {
  const [workouts, setWorkouts] = useState<WorkoutRecord[]>([]);
  const [isFetching, setIsFetching] = useState(true);

  useEffect(() => {
    let isActive = true;

    const loadWorkouts = async () => {
      setIsFetching(true);

      const collected: WorkoutRecord[] = [];
      let nextCursor: string | undefined = undefined;
      let hasMore = true;

      while (hasMore) {
        const result: { data?: unknown } = await store.dispatch(
          apiSlice.endpoints.getUserWorkouts.initiate({
            userId,
            from: startOfRange.toISOString(),
            to: endOfRange.toISOString(),
            limit: 100,
            cursor: nextCursor
          })
        );

        if ('data' in result) {
          const { items: pageData, pagination } = normalizeCursorPage<WorkoutRecord>(result.data);
          collected.push(...pageData);

          const nextPageCursor = pagination?.next_cursor ?? undefined;
          hasMore = Boolean(pagination?.has_more) && Boolean(nextPageCursor);
          nextCursor = nextPageCursor;

          if (!pagination?.has_more || !nextPageCursor) {
            hasMore = false;
          }
        } else {
          hasMore = false;
        }

        const unsubscribe = (result as unknown as { unsubscribe?: () => void }).unsubscribe;
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      }

      if (isActive) {
        setWorkouts(collected);
        setIsFetching(false);
      }
    };

    void loadWorkouts();

    return () => {
      isActive = false;
    };
  }, [userId, startOfRange, endOfRange]);

  return { workouts, isFetching };
}

export default function WorkoutsTab({ userId }: WorkoutsTabProps) {
  const { t } = useTranslation();
  const [selectedRange, setSelectedRange] = useState<WorkoutDayRange>(30);
  const { start: startOfRange, end: endOfRange } = useMemo(() => getWorkoutDateRange(selectedRange), [selectedRange]);

  const { workouts, isFetching } = useWorkoutSummaryData(userId, startOfRange, endOfRange);
  const filteredWorkouts: WorkoutRecord[] = useMemo(() => {
    return workouts.filter((workout) => {
      const workoutDateValue = getValue(workout, ['start_time', 'start_datetime', 'startDate', 'date']);
      if (!workoutDateValue) return false;

      const workoutDate = moment(String(workoutDateValue));
      return workoutDate.isValid() && workoutDate.isBetween(startOfRange, endOfRange, 'day', '[]');
    });
  }, [selectedRange, workouts]);

  const summary: WorkoutSummary = useMemo(() => {
    return filteredWorkouts.reduce<WorkoutSummary>(
      (acc, workout) => {
        const duration = toNumber(getValue(workout, ['duration_seconds', 'duration'])) ?? 0;
        const calories = toNumber(getValue(workout, ['calories_kcal', 'calories'])) ?? 0;
        const distance = toNumber(getValue(workout, ['distance_m', 'distance_meters', 'distance'])) ?? 0;

        acc.count += 1;
        acc.totalDuration += duration;
        acc.totalCalories += calories;
        acc.totalDistance += distance;

        return acc;
      },
      {
        count: 0,
        totalDuration: 0,
        totalCalories: 0,
        totalDistance: 0
      }
    );
  }, [filteredWorkouts]);

  const [allWorkoutsPage, setAllWorkoutsPage] = useState(1);
  const [allWorkoutsByPage, setAllWorkoutsByPage] = useState<Record<number, { items: WorkoutRecord[]; nextCursor: string | null }>>({});
  const [allWorkoutsLoading, setAllWorkoutsLoading] = useState(false);

  const currentPage = allWorkoutsPage;
  const currentPageData = allWorkoutsByPage[currentPage] ?? { items: [], nextCursor: null };
  const hasNextPage = Boolean(currentPageData.nextCursor);

  useEffect(() => {
    setAllWorkoutsPage(1);
    setAllWorkoutsByPage({});
    setAllWorkoutsLoading(false);
  }, [userId]);

  useEffect(() => {
    const page = currentPage;

    if (allWorkoutsByPage[page]) {
      setAllWorkoutsLoading(false);
      return;
    }

    let isActive = true;
    const cursor = page === 1 ? undefined : allWorkoutsByPage[page - 1]?.nextCursor ?? undefined;

    if (page > 1 && !cursor) {
      return;
    }

    const loadPage = async () => {
      setAllWorkoutsLoading(true);

      const result = await store.dispatch(
        apiSlice.endpoints.getUserWorkouts.initiate({
          userId,
          from: moment(0).toISOString(),
          to: moment().endOf('day').toISOString(),
          limit: 10,
          cursor
        })
      );

      if ('data' in result) {
        const { items, pagination } = normalizeCursorPage<WorkoutRecord>(result.data);
        const nextCursor = pagination?.next_cursor ?? null;

        if (isActive) {
          setAllWorkoutsByPage((prev) => ({
            ...prev,
            [page]: { items, nextCursor }
          }));
        }
      }

      if (isActive) {
        setAllWorkoutsLoading(false);
      }

      const unsubscribe = (result as unknown as { unsubscribe?: () => void }).unsubscribe;
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };

    void loadPage();

    return () => {
      isActive = false;
    };
  }, [currentPage, userId, allWorkoutsByPage]);

  function getWorkoutType(record: WorkoutRecord): string {
    const label = getValue(record, ['workout_type', 'type', 'category', 'activity_type', 'name']);
    if (typeof label === 'string' && label.trim()) return label.trim();
    return 'Workout';
  }

  function getWorkoutDate(record: WorkoutRecord): string {
    const value = getValue(record, ['start_time', 'start_datetime', 'startDate', 'date']);
    if (!value) return '—';

    const date = moment(String(value), moment.ISO_8601, true);
    return date.isValid() ? date.locale(i18n.language).format('ll') : '—';
  }

  function getWorkoutStats(record: WorkoutRecord) {
    return {
      duration: toNumber(getValue(record, ['duration_seconds', 'duration'])) ?? 0,
      calories: toNumber(getValue(record, ['calories_kcal', 'calories'])) ?? 0,
      averageHeartRate: toNumber(getValue(record, ['avg_heart_rate_bpm', 'average_heart_rate_bpm', 'avg_heart_rate', 'heart_rate_avg'])) ?? 0,
      distance: toNumber(getValue(record, ['distance_m', 'distance_meters', 'distance'])) ?? 0
    };
  }

  function getWorkoutSource(record: WorkoutRecord): string | null {
    const source = getValue(record, ['source', 'source_name', 'provider', 'data_source']);

    if (typeof source === 'string' && source.trim()) return source.trim();

    if (source && typeof source === 'object') {
      const nestedSource = source as { source?: unknown; provider?: unknown; name?: unknown };
      const provider = typeof nestedSource.provider === 'string' ? nestedSource.provider.trim() : '';
      const sourceName = typeof nestedSource.source === 'string' ? nestedSource.source.trim() : '';

      if (provider) return provider;
      if (sourceName) return sourceName;
    }

    return null;
  }

  function WorkoutRow({ workout }: { workout: WorkoutRecord }) {
    const workoutType = getWorkoutType(workout);
    const workoutDate = getWorkoutDate(workout);
    const stats = getWorkoutStats(workout);
    const workoutStyle = getWorkoutStyle(workoutType);
    const displayName = workoutStyle.label || workoutType;
    const workoutSource = getWorkoutSource(workout);

    return (
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          overflow: 'hidden',
          bgcolor: 'background.paper'
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            px: 2,
            py: 1.5,
            textAlign: 'left'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: 1, minWidth: 0, gap: 2 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 132, flexShrink: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    minWidth: 40,
                    borderRadius: 2,
                    bgcolor: workoutStyle.backgroundColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: workoutStyle.color,
                    flexShrink: 0,
                    fontSize: '1.1rem'
                  }}
                >
                  {workoutStyle.emoji}
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {displayName}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {workoutDate}
                  </Typography>
                </Box>
              </Box>

              {workoutSource && (
                <Box sx={{ pt: 1 }}>
                  <ProviderChip provider={workoutSource} />
                </Box>
              )}
            </Box>

            <Grid container spacing={1.5} sx={{ flex: 1, minWidth: 0, alignItems: 'center', alignSelf: 'center' }}>
              <Grid item xs={12} sm={4}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ justifyContent: 'center', textAlign: 'left' }}>
                  <Timer size={16} strokeWidth={1.8} style={{ color: '#0ea5e9' }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{formatDuration(stats.duration)}</Typography>
                    <Typography variant="caption" color="text.secondary">Duration</Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ justifyContent: 'center', textAlign: 'left' }}>
                  <Flame size={16} strokeWidth={1.8} style={{ color: '#f97316' }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>{`${Math.round(stats.calories).toLocaleString()} kcal`}</Typography>
                    <Typography variant="caption" color="text.secondary">Calories</Typography>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} sm={4}>
                <Stack direction="row" spacing={0.75} alignItems="center" sx={{ justifyContent: 'center', textAlign: 'left' }}>
                  <Heart size={16} strokeWidth={1.8} style={{ color: '#f43f5e' }} />
                  <Box>
                    <Typography variant="body1" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                      {stats.averageHeartRate > 0 ? `${Math.round(stats.averageHeartRate)} bpm` : '—'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Avg HR</Typography>
                  </Box>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    );
  }

  const summaryLoading = isFetching;

  return (
    <Stack spacing={2}>
      <MainCard
        title={t('summary', 'Summary')}
        secondary={
          <PatientDateRangeSelector value={selectedRange} onChange={setSelectedRange} />
        }
      >
        {summaryLoading ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((item) => (
              <Grid item xs={12} sm={6} lg={3} key={item}>
                <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                  <Stack spacing={1.5}>
                    <Box sx={{ p: 1, width: 36, height: 36, minWidth: 36, borderRadius: 2, bgcolor: 'action.hover' }} />
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                      <Box sx={{ width: '40%', height: 28, borderRadius: 1, bgcolor: 'action.hover' }} />
                      <Box sx={{ width: '58%', height: 14, borderRadius: 1, bgcolor: 'action.hover' }} />
                    </Box>
                  </Stack>
                </Box>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} lg={3}>
              <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Stack spacing={1.5}>
                  <Box sx={{ p: 1, width: 36, height: 36, minWidth: 36, borderRadius: 2, bgcolor: 'rgba(99, 102, 241, 0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Dumbbell size={20} strokeWidth={1.8} style={{ color: '#818cf8' }} />
                  </Box>
                  <Typography variant="h4" sx={{ lineHeight: 1 }}>
                    {summary.count}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('workouts', 'Workouts')}
                  </Typography>
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Stack spacing={1.5}>
                  <Box sx={{ p: 1, width: 36, height: 36, minWidth: 36, borderRadius: 2, bgcolor: 'rgba(14, 165, 233, 0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Timer size={20} strokeWidth={1.8} style={{ color: '#38bdf8' }} />
                  </Box>
                  <Typography variant="h4" sx={{ lineHeight: 1 }}>
                    {formatDuration(summary.totalDuration)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('total_time', 'Total Time')}
                  </Typography>
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Stack spacing={1.5}>
                  <Box sx={{ p: 1, width: 36, height: 36, minWidth: 36, borderRadius: 2, bgcolor: 'rgba(249, 115, 22, 0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Flame size={20} strokeWidth={1.8} style={{ color: '#fb923c' }} />
                  </Box>
                  <Typography variant="h4" sx={{ lineHeight: 1 }}>
                    {Math.round(summary.totalCalories).toLocaleString()} kcal
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('calories', 'Calories')}
                  </Typography>
                </Stack>
              </Box>
            </Grid>

            <Grid item xs={12} sm={6} lg={3}>
              <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Stack spacing={1.5}>
                  <Box sx={{ p: 1, width: 36, height: 36, minWidth: 36, borderRadius: 2, bgcolor: 'rgba(168, 85, 247, 0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <MoveHorizontal size={20} strokeWidth={1.8} style={{ color: '#c084fc' }} />
                  </Box>
                  <Typography variant="h4" sx={{ lineHeight: 1 }}>
                    {formatDistance(summary.totalDistance)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('distance', 'Distance')}
                  </Typography>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        )}
      </MainCard>

      <MainCard
        title={t('all_workouts', 'All Workouts')}
        secondary={
          <Typography variant="caption" color="text.secondary">
            {t('page', 'Page')} {currentPage}
          </Typography>
        }
      >
        <Stack spacing={1.5}>
          {allWorkoutsLoading ? (
            <Stack spacing={1.5}>
              {[1, 2, 3, 4, 5].map((item) => (
                <Box
                  key={item}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: 'background.paper'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, px: 2, py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', flex: 1, minWidth: 0, gap: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, minWidth: 132, flexShrink: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box sx={{ width: 40, height: 40, minWidth: 40, borderRadius: 2, bgcolor: 'action.hover', flexShrink: 0 }} />

                          <Box sx={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            <Box sx={{ width: 92, height: 16, borderRadius: 1, bgcolor: 'action.hover' }} />
                            <Box sx={{ width: 64, height: 12, borderRadius: 1, bgcolor: 'action.hover' }} />
                          </Box>
                        </Box>

                        <Box sx={{ pt: 1, width: 72, height: 28, borderRadius: 999, bgcolor: 'action.hover' }} />
                      </Box>

                      <Grid container spacing={1.5} sx={{ flex: 1, minWidth: 0, alignItems: 'center', alignSelf: 'center' }}>
                        {[1, 2, 3].map((statItem) => (
                          <Grid item xs={12} sm={4} key={statItem}>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ justifyContent: 'center' }}>
                              <Box sx={{ width: 16, height: 16, borderRadius: '50%', bgcolor: 'action.hover', flexShrink: 0 }} />
                              <Box>
                                <Box sx={{ width: 72, height: 16, borderRadius: 1, bgcolor: 'action.hover', mb: 0.5 }} />
                                <Box sx={{ width: 44, height: 12, borderRadius: 1, bgcolor: 'action.hover' }} />
                              </Box>
                            </Stack>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Stack>
          ) : currentPageData.items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No workout data available
            </Typography>
          ) : (
            <Stack spacing={1.5}>
              {currentPageData.items.map((workout, index) => (
                <WorkoutRow key={String(getValue(workout, ['id', 'workout_id', 'uuid']) ?? `${getWorkoutType(workout)}-${getWorkoutDate(workout)}-${index}`)} workout={workout} />
              ))}
            </Stack>
          )}

          <Stack spacing={2} sx={{ pt: 1.5 }} alignItems="flex-end">
            <Pagination
              count={Math.max(1, currentPage + (hasNextPage ? 1 : 0))}
              size="medium"
              page={currentPage}
              variant="combined"
              color="primary"
              onChange={(_, nextPage) => {
                const targetPage = nextPage;
                if (!allWorkoutsLoading && targetPage !== currentPage) {
                  setAllWorkoutsPage(targetPage);
                }
              }}
            />
          </Stack>
        </Stack>
      </MainCard>
    </Stack>
  );
}
